import { useState, useRef, useEffect, useCallback } from "react";
import { trackEvent, trackConversionWithJourney, identifyUser, ANALYTICS_EVENTS } from '@/lib/analytics';
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Navbar, NavBody, NavItems, MobileNav, NavbarLogo, NavbarButton, MobileNavHeader, MobileNavToggle, MobileNavMenu } from "@/components/ui/resizable-navbar";
import { MobileMenuContent } from "@/components/MobileMenuContent";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import Footer from "@/components/Footer";
import logoWhite from "@/assets/logo-white.svg";
import logoBlack from "@/assets/logo-black.svg";
import { useWaitlistModal } from "@/components/waitlist/WaitlistContext";
import {
  FormInput,
  FormTextarea,
  FormLabel,
  FormContainer,
  FormSubmitButton,
  SuccessState,
  PageSeo,
  DynamicCtaFooter,
} from "@/components/ds";
import { usePrerenderReadyImmediate } from "@/hooks/usePrerenderReady";
import { useScrollDepth } from "@/hooks/useScrollDepth";
import { SITE_URL, buildUrl } from "@/config/site";
import { useCompanySettings } from "@/hooks/useCompanySettings";

const commonTLDs = new Set([
  'com', 'org', 'net', 'edu', 'gov', 'mil', 'int',
  'co', 'io', 'ai', 'app', 'dev', 'me', 'info', 'biz',
  'us', 'uk', 'ca', 'au', 'de', 'fr', 'es', 'it', 'nl',
  'be', 'at', 'ch', 'se', 'no', 'dk', 'fi', 'pt', 'ie',
  'nz', 'za', 'in', 'jp', 'kr', 'cn', 'sg', 'hk', 'tw',
  'br', 'mx', 'ar', 'cl', 'ru', 'pl', 'cz', 'hu', 'ro',
  'bg', 'hr', 'sk', 'si', 'lt', 'lv', 'ee', 'is', 'lu',
  'ae', 'il', 'sa', 'qa', 'ke', 'ng', 'gh', 'tz', 'ug',
  'xyz', 'online', 'site', 'tech', 'store', 'cloud', 'pro',
  'live', 'world', 'email', 'digital', 'global', 'agency',
  'design', 'studio', 'group', 'fund', 'capital', 'finance',
  'ventures', 'partners', 'consulting', 'solutions', 'systems',
  'network', 'media', 'blog', 'news', 'shop', 'market',
  'money', 'exchange', 'investments', 'properties', 'holdings',
  'museum', 'travel', 'jobs', 'mobi', 'tel', 'name', 'coop',
]);

const contactSchema = z.object({
  name: z
    .string()
    .trim()
    .min(2, "Name must be at least 2 characters")
    .max(100, "Name must be less than 100 characters")
    .regex(/^[a-zA-Z\s\-']+$/, "Name can only contain letters, spaces, hyphens, and apostrophes")
    .refine(
      (val) => val.trim().split(/\s+/).length >= 2,
      "Please enter your first and last name"
    ),
  email: z
    .string()
    .trim()
    .email("Please enter a valid email address")
    .max(255, "Email must be less than 255 characters")
    .transform((val) => val.toLowerCase())
    .refine(
      (val) => {
        const tld = val.split('.').pop();
        if (!tld) return false;
        return commonTLDs.has(tld);
      },
      "Please enter a valid email domain (e.g. .com, .co.za, .org)"
    ),
  subject: z.string().trim().min(2, "Subject must be at least 2 characters").max(200, "Subject must be less than 200 characters"),
  message: z.string().trim().min(2, "Please enter a message").max(2000, "Message must be less than 2000 characters")
});

type ContactFormData = z.infer<typeof contactSchema>;

const CONTACT_FIELDS = ['name', 'email', 'subject', 'message'] as const;

const NavButtons = ({
  visible,
  onWaitlistClick
}: {
  visible?: boolean;
  onWaitlistClick: (triggerElement?: string) => void;
}) => (
  <div className="flex items-center gap-8">
    <NavbarButton href="/contact" variant="secondary" visible={visible}>
      Contact
    </NavbarButton>
    <NavbarButton variant="primary" visible={visible} onClick={() => onWaitlistClick('navbar_cta')}>
      Join Waitlist
    </NavbarButton>
  </div>
);

export default function Contact() {
  useScrollDepth();
  const { data: company } = useCompanySettings();

  const navItems = [
    { name: "Invest", link: "/invest" },
    { name: "Learn", link: "/learn" },
    { name: "About", link: "/about" }
  ];

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const { toast } = useToast();
  const { openModal } = useWaitlistModal();

  // ⚠️ ANALYTICS: Form tracking state
  const [hasTrackedFormStart, setHasTrackedFormStart] = useState(false);
  const formStartTime = useRef<number>(0);

  // ⚠️ ANALYTICS: Field-level tracking refs (FIX 3)
  const enteredFields = useRef(new Set<string>());
  const validatedFields = useRef(new Set<string>());
  const hasTrackedFormCompleted = useRef(false);
  const formSubmittedRef = useRef(false);
  // ⚠️ ANALYTICS: FIX 3 (BUG-FIX-12) — only fire validation when value changes
  const previousFieldValues = useRef(new Map<string, string>());

  // Signal to Prerender.io that the page is ready (static form content)
  usePrerenderReadyImmediate();

  const {
    register,
    handleSubmit,
    reset,
    trigger,
    formState: { errors },
  } = useForm<ContactFormData>({
    resolver: zodResolver(contactSchema),
    mode: "onTouched",
    defaultValues: {
      name: "",
      email: "",
      subject: "",
      message: ""
    }
  });

  // ⚠️ ANALYTICS: Track form start
  const handleFormStart = () => {
    if (!hasTrackedFormStart) {
      setHasTrackedFormStart(true);
      formStartTime.current = Date.now();

      trackEvent(ANALYTICS_EVENTS.FORM_STARTED, {
        form_id: 'contact-form',
        form_name: 'Contact Form',
        first_field: 'name',
      });
    }
  };

  // ⚠️ ANALYTICS: Track field entry (fires once per field on first input) (FIX 3)
  const handleFieldChange = useCallback((fieldName: string) => {
    if (!enteredFields.current.has(fieldName)) {
      enteredFields.current.add(fieldName);
      trackEvent(ANALYTICS_EVENTS.CONTACT_FIELD_ENTERED, {
        field_name: fieldName,
        form_id: 'contact-form',
        fields_entered_count: enteredFields.current.size,
      });
    }
  }, []);

  // Check if all 4 fields validated → fire CONTACT_FORM_COMPLETED
  const checkFormCompleted = useCallback(() => {
    if (
      CONTACT_FIELDS.every(f => validatedFields.current.has(f)) &&
      !hasTrackedFormCompleted.current
    ) {
      hasTrackedFormCompleted.current = true;
      trackEvent(ANALYTICS_EVENTS.CONTACT_FORM_COMPLETED, {
        form_id: 'contact-form',
        time_to_complete_ms: formStartTime.current > 0 ? Date.now() - formStartTime.current : 0,
      });
    }
  }, []);

  // ⚠️ ANALYTICS: Track field validation on blur (FIX 5 + FIX 3 BUG-FIX-12 – skip empty & unchanged)
  const handleFieldBlur = useCallback(async (fieldName: string, currentValue: string) => {
    // Don't fire validation events for empty fields (focus-then-blur noise)
    if (!currentValue || !currentValue.trim()) return;

    // FIX 3 (BUG-FIX-12): Skip if value hasn't changed since last blur
    const trimmed = currentValue.trim();
    const prev = previousFieldValues.current.get(fieldName);
    if (prev === trimmed) return;
    previousFieldValues.current.set(fieldName, trimmed);

    const isValid = await trigger(fieldName as keyof ContactFormData);
    trackEvent(ANALYTICS_EVENTS.CONTACT_FIELD_VALIDATED, {
      field_name: fieldName,
      is_valid: isValid,
      form_id: 'contact-form',
      field_length: trimmed.length,
    });
    if (isValid) {
      validatedFields.current.add(fieldName);
    } else {
      validatedFields.current.delete(fieldName);
    }
    checkFormCompleted();
  }, [trigger, checkFormCompleted]);

  // ⚠️ ANALYTICS: Track submit button click (FIX 3)
  const handleSubmitClick = useCallback(() => {
    trackEvent(ANALYTICS_EVENTS.CONTACT_SUBMIT_CLICKED, {
      form_id: 'contact-form',
      fields_entered: Array.from(enteredFields.current),
      fields_validated: Array.from(validatedFields.current),
      time_to_submit_click_ms: formStartTime.current > 0 ? Date.now() - formStartTime.current : 0,
    });
  }, []);

  // ⚠️ ANALYTICS: Abandonment tracking on unmount (FIX 3)
  useEffect(() => {
    return () => {
      if (hasTrackedFormStart && !formSubmittedRef.current && enteredFields.current.size > 0) {
        trackEvent(ANALYTICS_EVENTS.CONTACT_FORM_ABANDONED, {
          form_id: 'contact-form',
          fields_entered: Array.from(enteredFields.current),
          fields_validated: Array.from(validatedFields.current),
          time_before_abandon_ms: formStartTime.current > 0 ? Date.now() - formStartTime.current : 0,
        });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasTrackedFormStart]);

  // Register fields with wrapped handlers
  const nameRegister = register("name");
  const emailRegister = register("email");
  const subjectRegister = register("subject");
  const messageRegister = register("message");

  const onSubmit = async (data: ContactFormData) => {
    setIsSubmitting(true);

    // ⚠️ ANALYTICS: Best-effort DB write for Segment identity UUID
    // Wrapped in its own try/catch so email send always proceeds
    let contactRowId: string | null = crypto.randomUUID();
    try {
      const { error: dbError } = await supabase.from("contact_submissions").insert({
        id: contactRowId,
        full_name: data.name,
        email: data.email,
        subject: data.subject,
        message: data.message,
      });
      if (dbError) {
        console.error("Contact submission DB insert failed:", dbError);
        contactRowId = null;
      }
    } catch (dbErr) {
      console.error("Contact DB write failed (non-blocking):", dbErr);
      contactRowId = null;
    }

    try {
      const { error } = await supabase.functions.invoke("send-contact-email", {
        body: data
      });
      if (error) {
        throw error;
      }

      // ⚠️ ANALYTICS: Mark as submitted to prevent abandonment event
      formSubmittedRef.current = true;

      // ⚠️ ANALYTICS: Track contact form conversion
      const completionTime = formStartTime.current > 0
        ? Date.now() - formStartTime.current
        : 0;

      trackConversionWithJourney(ANALYTICS_EVENTS.CONTACT_FORM_SUBMITTED, {
        form_id: 'contact-form',
        subject: data.subject,
        form_completion_time_ms: completionTime,
      });

      // ⚠️ ANALYTICS: Identify user using row UUID from contact_submissions
      if (contactRowId) {
        await identifyUser(contactRowId, data.email, {
          name: data.name,
          first_name: data.name.split(' ')[0],
          last_name: data.name.split(' ').slice(1).join(' ') || undefined,
          signup_source: 'contact',
          signup_page: '/contact',
          created_at: new Date().toISOString(),
        });
      }

      setIsSuccess(true);
      reset();
    } catch (err) {
      console.error("Contact form error:", err);

      // ⚠️ ANALYTICS: Track edge function error (FIX 5)
      trackEvent(ANALYTICS_EVENTS.EDGE_FUNCTION_ERROR, {
        function_name: 'send-contact-email',
        error_message: err instanceof Error ? err.message : String(err),
        context: 'contact_form_submission',
      });

      toast({
        title: "Error",
        description: "Something went wrong. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <PageSeo pageSlug="/contact">
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "ContactPage",
                "@id": buildUrl('/contact'),
                "url": buildUrl('/contact'),
                "name": "Contact Us | Fedgroup Private Markets",
                "description": "Get in touch with Fedgroup Private Markets. We're here to help with your investment questions.",
                "isPartOf": { "@type": "WebSite", "@id": `${SITE_URL}/#website` }
              },
              {
                "@type": "LocalBusiness",
                "@id": `${SITE_URL}/#localbusiness`,
                "name": company?.company_name || "Fedgroup Private Markets",
                "parentOrganization": { "@type": "Organization", "@id": `${SITE_URL}/#organization` },
                "telephone": company?.company_phone || "+27-11-305-2300",
                "email": company?.company_email || "privatemarkets@fedgroup.co.za",
                "address": {
                  "@type": "PostalAddress",
                  "streetAddress": company?.company_address_street || "89 Bute Lane, Sandown",
                  "addressLocality": company?.company_address_city || "Sandton",
                  "addressRegion": company?.company_address_region || "Gauteng",
                  "postalCode": company?.company_address_postal || "2196",
                  "addressCountry": company?.company_address_country || "ZA"
                },
                "geo": {
                  "@type": "GeoCoordinates",
                  "latitude": -26.1076,
                  "longitude": 28.0567
                },
                "openingHoursSpecification": {
                  "@type": "OpeningHoursSpecification",
                  "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
                  "opens": "08:00",
                  "closes": "17:00"
                },
                "url": company?.company_url || SITE_URL
              },
              {
                "@type": "BreadcrumbList",
                "itemListElement": [
                  { "@type": "ListItem", "position": 1, "name": "Home", "item": buildUrl('/') },
                  { "@type": "ListItem", "position": 2, "name": "Contact", "item": buildUrl('/contact') }
                ]
              }
            ]
          })}
        </script>
      </PageSeo>

      <div className="relative w-full min-h-screen bg-background">
        <Navbar forceVisible mobileMenuOpen={isMobileMenuOpen}>
          {/* Desktop Navigation */}
          <NavBody>
            <NavbarLogo logoWhite={logoWhite} logoBlack={logoBlack} />
            <NavItems items={navItems} />
            <NavButtons onWaitlistClick={openModal} />
          </NavBody>

          {/* Mobile Navigation */}
          <MobileNav>
            <MobileNavHeader>
              <NavbarLogo logoWhite={logoWhite} logoBlack={logoBlack} />
              <MobileNavToggle isOpen={isMobileMenuOpen} onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} />
            </MobileNavHeader>

            <MobileNavMenu isOpen={isMobileMenuOpen} onClose={() => setIsMobileMenuOpen(false)}>
              <MobileMenuContent
                navItems={navItems}
                onItemClick={() => setIsMobileMenuOpen(false)}
                onWaitlistClick={openModal}
              />
            </MobileNavMenu>
          </MobileNav>
        </Navbar>

        {/* Main Content */}
        <main className="pt-20 pb-16 md:pt-24 md:pb-24">
          <div className="container mx-auto px-4 md:px-8 max-w-[1400px]">
            {/* Section Header */}
            <div className="mb-10">
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-foreground max-w-3xl">
                Get in touch.
              </h1>
            </div>

            {/* Two Column Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
              {/* Form Column */}
              <div>
                {isSuccess ? (
                  <FormContainer>
                    <SuccessState
                      title="Message Sent"
                      description="Thank you for reaching out. We'll get back to you soon."
                      action={{
                        label: "Send Another Message",
                        onClick: () => setIsSuccess(false)
                      }}
                    />
                  </FormContainer>
                ) : (
                  <FormContainer>
                    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                      {/* Name Field */}
                      <div className="space-y-2">
                        <FormLabel htmlFor="name">Name</FormLabel>
                        <FormInput
                          id="name"
                          placeholder="John Smith"
                          inputSize="lg"
                          onFocus={handleFormStart}
                          {...nameRegister}
                          onChange={(e) => {
                            nameRegister.onChange(e);
                            handleFieldChange('name');
                          }}
                          onBlur={(e) => {
                            nameRegister.onBlur(e);
                            handleFieldBlur('name', e.target.value);
                          }}
                        />
                        {errors.name && (
                          <p className="text-12 text-destructive">{errors.name.message}</p>
                        )}
                      </div>

                      {/* Email Field */}
                      <div className="space-y-2">
                        <FormLabel htmlFor="email">Email Address</FormLabel>
                        <FormInput
                          id="email"
                          type="email"
                          placeholder="john@example.com"
                          inputSize="lg"
                          {...emailRegister}
                          onChange={(e) => {
                            emailRegister.onChange(e);
                            handleFieldChange('email');
                          }}
                          onBlur={(e) => {
                            emailRegister.onBlur(e);
                            handleFieldBlur('email', e.target.value);
                          }}
                        />
                        {errors.email && (
                          <p className="text-12 text-destructive">{errors.email.message}</p>
                        )}
                      </div>

                      {/* Subject Field */}
                      <div className="space-y-2">
                        <FormLabel htmlFor="subject">Subject</FormLabel>
                        <FormInput
                          id="subject"
                          placeholder="How can we help?"
                          inputSize="lg"
                          {...subjectRegister}
                          onChange={(e) => {
                            subjectRegister.onChange(e);
                            handleFieldChange('subject');
                          }}
                          onBlur={(e) => {
                            subjectRegister.onBlur(e);
                            handleFieldBlur('subject', e.target.value);
                          }}
                        />
                        {errors.subject && (
                          <p className="text-12 text-destructive">{errors.subject.message}</p>
                        )}
                      </div>

                      {/* Message Field */}
                      <div className="space-y-2">
                        <FormLabel htmlFor="message">Message</FormLabel>
                        <FormTextarea
                          id="message"
                          placeholder="Tell us more about your enquiry..."
                          {...messageRegister}
                          onChange={(e) => {
                            messageRegister.onChange(e);
                            handleFieldChange('message');
                          }}
                          onBlur={(e) => {
                            messageRegister.onBlur(e);
                            handleFieldBlur('message', e.target.value);
                          }}
                        />
                        {errors.message && (
                          <p className="text-12 text-destructive">{errors.message.message}</p>
                        )}
                      </div>

                      <FormSubmitButton
                        type="submit"
                        isLoading={isSubmitting}
                        loadingText="Sending..."
                        onClick={handleSubmitClick}
                      >
                        Send Message
                      </FormSubmitButton>
                    </form>
                  </FormContainer>
                )}
              </div>

              {/* Contact Info Column */}
              <div className="lg:pl-8">
              <div className="space-y-12">
                  <div>
                    <h2 className="text-lg font-bold text-carbon mb-4">Office</h2>
                    <p className="text-coal">
                      {company?.company_address_street || '89 Bute Lane, Sandown'}<br />
                      {company?.company_address_city || 'Sandton'}, {company?.company_address_postal || '2196'}<br />
                      South Africa
                    </p>
                  </div>

                  <div>
                    <h2 className="text-lg font-bold text-carbon mb-4">Contact</h2>
                    <div className="space-y-2">
                      <p className="text-coal">
                        <span className="text-carbon">Phone:</span>{' '}
                        <a href={`tel:${(company?.company_phone || '+27-11-305-2300').replace(/\s/g, '')}`} className="hover:text-carbon transition-colors">
                          {company?.company_phone || '+27 (11) 305 2300'}
                        </a>
                      </p>
                      <p className="text-coal">
                        <span className="text-carbon">Email:</span>{' '}
                        <a href={`mailto:${company?.company_email || 'privatemarkets@fedgroup.co.za'}`} className="hover:text-carbon transition-colors">
                          {company?.company_email || 'privatemarkets@fedgroup.co.za'}
                        </a>
                      </p>
                    </div>
                  </div>

                  <div>
                    <h2 className="text-lg font-bold text-carbon mb-4">Hours</h2>
                    <p className="text-coal">
                      Monday – Friday<br />
                      08:00 – 17:00 SAST
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>

        <DynamicCtaFooter pageSlug="/contact" />

        <Footer />
      </div>
    </>
  );
}
